/**
 * Game Selection Service
 * Handles game selection logic and mutual agreement
 */

import { database } from './firebase';
import { ref, update, get, serverTimestamp } from 'firebase/database';
import { notifyGameSelected, notifyGameStarted } from './notificationService';
import { getAllGames } from '../config/games';
import { createGame } from './gameService';
import { createInitialGameState as createTicTacToeState } from '../games/tictactoe/logic';
import { createInitialGameState as createRPSState } from '../games/rps/logic';
import { createInitialGameState as createConnect4State } from '../games/connect4/logic';
import { createInitialGameState as createDotsAndBoxesState } from '../games/dotsandboxes/logic';
import { createInitialGameState as createCheckersState } from '../games/checkers/logic';
import { createInitialGameState as createBattleshipState } from '../games/battleship/logic';
import { createInitialGameState as createChessState } from '../games/chess/logic';

/**
 * Sets a player's game selection
 * Both players must select the same game for it to be locked in
 *
 * @param {string} roomCode - The room code
 * @param {string} userId - User ID of the player
 * @param {string} gameId - ID of the selected game
 * @returns {Promise<void>}
 */
export const selectGame = async (roomCode, userId, gameId) => {
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);

    if (!snapshot.exists()) {
      throw new Error('Room not found');
    }

    const roomData = snapshot.val();
    const isHost = roomData.host.userId === userId;
    const isGuest = roomData.guest && roomData.guest.userId === userId;

    if (!isHost && !isGuest) {
      throw new Error('You are not a player in this room');
    }

    // Update the player's selection
    const updates = {};
    let recipientId = null;

    if (isHost) {
      updates['host/gameSelection'] = gameId;
      recipientId = roomData.guest ? roomData.guest.userId : null;
    } else if (isGuest) {
      updates['guest/gameSelection'] = gameId;
      recipientId = roomData.host.userId;
    }

    updates['lastActivity'] = serverTimestamp();

    await update(roomRef, updates);

    // Game selected notification removed as per user request

    // Check if both players selected the same game
    const updatedSnapshot = await get(roomRef);
    const updatedData = updatedSnapshot.val();

    const hostSelection = updatedData.host.gameSelection;
    const guestSelection = updatedData.guest?.gameSelection;

    // If both players selected the same game, lock it in
    if (hostSelection && guestSelection && hostSelection === guestSelection) {
      await update(roomRef, {
        gameSelected: hostSelection,
        status: 'game_selected',
        lastActivity: serverTimestamp()
      });
    }
    // If they selected different games, clear the locked game
    else if (hostSelection && guestSelection && hostSelection !== guestSelection) {
      await update(roomRef, {
        gameSelected: null,
        status: 'waiting',
        lastActivity: serverTimestamp()
      });
    }

  } catch (error) {
    console.error('Error selecting game:', error);
    throw error;
  }
};

/**
 * Clears a player's game selection
 *
 * @param {string} roomCode - The room code
 * @param {string} userId - User ID of the player
 * @returns {Promise<void>}
 */
export const clearGameSelection = async (roomCode, userId) => {
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);

    if (!snapshot.exists()) {
      throw new Error('Room not found');
    }

    const roomData = snapshot.val();
    const isHost = roomData.host.userId === userId;

    const updates = {};

    if (isHost) {
      updates['host/gameSelection'] = null;
    } else {
      updates['guest/gameSelection'] = null;
    }

    updates['lastActivity'] = serverTimestamp();

    // Only clear the locked game selection if there was one
    if (roomData.gameSelected) {
      updates['gameSelected'] = null;
      updates['status'] = 'waiting';
    }

    await update(roomRef, updates);

  } catch (error) {
    console.error('Error clearing game selection:', error);
    throw error;
  }
};

/**
 * Starts the selected game
 * This will transition the room to playing status
 *
 * @param {string} roomCode - The room code
 * @returns {Promise<void>}
 */
export const startGame = async (roomCode) => {
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);

    if (!snapshot.exists()) {
      throw new Error('Room not found');
    }

    const roomData = snapshot.val();

    if (!roomData.gameSelected) {
      throw new Error('No game selected');
    }

    if (!roomData.guest) {
      throw new Error('Waiting for second player to join');
    }

    // Create initial game state based on game type
    let initialGameState;
    if (roomData.gameSelected === 'tictactoe') {
      initialGameState = createTicTacToeState(
        roomData.host.userId,
        roomData.guest.userId
      );
    } else if (roomData.gameSelected === 'rps') {
      initialGameState = createRPSState(
        roomData.host.userId,
        roomData.guest.userId
      );
    } else if (roomData.gameSelected === 'connect4') {
      initialGameState = createConnect4State(
        roomData.host.userId,
        roomData.guest.userId
      );
    } else if (roomData.gameSelected === 'dotsandboxes') {
      initialGameState = createDotsAndBoxesState(
        roomData.host.userId,
        roomData.guest.userId
      );
    } else if (roomData.gameSelected === 'checkers') {
      // Legacy support for old checkers (default to American)
      initialGameState = createCheckersState(
        roomData.host.userId,
        roomData.guest.userId,
        'american'
      );
    } else if (roomData.gameSelected === 'americancheckers') {
      initialGameState = createCheckersState(
        roomData.host.userId,
        roomData.guest.userId,
        'american'
      );
    } else if (roomData.gameSelected === 'russiancheckers') {
      initialGameState = createCheckersState(
        roomData.host.userId,
        roomData.guest.userId,
        'russian'
      );
    } else if (roomData.gameSelected === 'battleship') {
      initialGameState = createBattleshipState(
        roomData.host.userId,
        roomData.guest.userId
      );
    } else if (roomData.gameSelected === 'chess') {
      initialGameState = createChessState(
        roomData.host.userId,
        roomData.guest.userId
      );
    } else {
      throw new Error(`Game type ${roomData.gameSelected} not implemented yet`);
    }

    // Create the game in Firebase
    await createGame(
      roomCode,
      roomData.gameSelected,
      initialGameState,
      {
        host: roomData.host,
        guest: roomData.guest,
      }
    );

    // Update room status
    await update(roomRef, {
      status: 'playing',
      startedAt: serverTimestamp(),
      lastActivity: serverTimestamp()
    });

    // Game started notifications removed as per user request

  } catch (error) {
    console.error('Error starting game:', error);
    throw error;
  }
};

/**
 * Checks if both players have selected the same game
 *
 * @param {Object} roomData - The room data object
 * @returns {boolean} True if both players selected the same game
 */
export const haveBothPlayersAgreed = (roomData) => {
  if (!roomData.host || !roomData.guest) {
    return false;
  }

  const hostSelection = roomData.host.gameSelection;
  const guestSelection = roomData.guest.gameSelection;

  return (
    hostSelection &&
    guestSelection &&
    hostSelection === guestSelection
  );
};

/**
 * Gets the agreed-upon game if both players selected the same one
 *
 * @param {Object} roomData - The room data object
 * @returns {string|null} The game ID if agreed, null otherwise
 */
export const getAgreedGame = (roomData) => {
  if (haveBothPlayersAgreed(roomData)) {
    return roomData.host.gameSelection;
  }
  return null;
};
